import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Table,
  Input,
  Button,
  message,
  Typography,
  Avatar,
  Tag
} from 'antd';
import { PlusOutlined, SearchOutlined, EditOutlined } from '@ant-design/icons';
import PageLayout from '../../../components/layout/PageLayout';
import masterService from '../../../services/masterService';
import { useUser } from '../../../context/UserContext';

const { Title, Text } = Typography;
const { Search } = Input;

const PaymentKeyList = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  
  const [paymentKeys, setPaymentKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
  });

  const columns = [
    {
      title: 'Payment Mode',
      dataIndex: 'pm_payment_mode',
      key: 'pm_payment_mode',
      sorter: true,
    },
    {
      title: 'API Key',
      dataIndex: 'api_key',
      key: 'api_key',
      render: (text) => (
        <Text code style={{ fontSize: '12px' }}>
          {text ? `${text.substring(0, 8)}...` : 'N/A'}
        </Text>
      ),
    },
    {
      title: 'Secret Key',
      dataIndex: 'api_secret',
      key: 'api_secret',
      render: (text) => (
        <Text code style={{ fontSize: '12px' }}>
          {text ? `${text.substring(0, 8)}...` : 'N/A'}
        </Text>
      ),
    },
    {
      title: 'Access Code',
      dataIndex: 'access_code',
      key: 'access_code',
      render: (text) => (
        <Text code style={{ fontSize: '12px' }}>
          {text ? `${text.substring(0, 8)}...` : 'N/A'}
        </Text>
      ),
    },
    {
      title: 'Payment URL',
      dataIndex: 'payment_url',
      key: 'payment_url',
      render: (text) => (
        <Text 
          style={{ 
            fontSize: '12px',
            maxWidth: '200px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            display: 'block'
          }}
          title={text}
        >
          {text || 'N/A'}
        </Text>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'pk_is_active',
      key: 'pk_is_active',
      render: (status) => (
        <Tag color={status === 'Y' ? 'green' : 'red'}>
          {status === 'Y' ? 'Active' : 'Inactive'}
        </Tag>
      ),
      filters: [
        { text: 'Active', value: 'Y' },
        { text: 'Inactive', value: 'N' },
      ],
      onFilter: (value, record) => record.pk_is_active === value,
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right',
      width: 100,
      render: (_, record) => (
        <Button
          type="link"
          icon={<EditOutlined />}
          onClick={() => handleEdit(record.payment_keys_id)}
          size="small"
        >
          Edit
        </Button>
      ),
    },
  ];

  const fetchPaymentKeys = useCallback(async (params = {}) => {
    setLoading(true);
    try {
      const response = await masterService.getPaymentKeys({
        perPage: pagination.pageSize,
        currentPage: pagination.current,
        searchText: searchText,
        ...params
      });

      if (response.data && response.data.status) {
        const { data, pagination: paginationData } = response.data.data;
        setPaymentKeys(data || []);
        setPagination(prev => ({
          ...prev,
          current: paginationData.currentPage,
          total: paginationData.total,
        }));
      } else {
        setPaymentKeys([]);
        message.error('Failed to fetch payment keys');
      }
    } catch (error) {
      console.error('Error fetching payment keys:', error);
      setPaymentKeys([]);
      message.error('Error fetching payment keys');
    } finally {
      setLoading(false);
    }
  }, [pagination.pageSize, pagination.current, searchText]);

  useEffect(() => {
    fetchPaymentKeys();
  }, [fetchPaymentKeys]);

  const handleSearch = (value) => {
    setSearchText(value);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleTableChange = (newPagination, filters, sorter) => {
    const { current, pageSize } = newPagination;
    
    if (current !== pagination.current || pageSize !== pagination.pageSize) {
      setPagination(prev => ({
        ...prev,
        current,
        pageSize,
      }));
    }
  };

  const handleAdd = () => {
    navigate('/payment/key/addKey');
  };

  const handleEdit = (paymentKeysId) => {
    navigate(`/payment/key/editKey/${paymentKeysId}`);
  };

  const headerContent = (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
        <Avatar
          src={user?.avatar}
          size={64}
          style={{ marginRight: '16px' }}
        >
          {user?.name?.charAt(0)?.toUpperCase()}
        </Avatar>
        <div>
          <Title level={4} style={{ margin: 0, marginBottom: '4px' }}>
            {user?.name || 'Admin'}
          </Title>
          <Text type="secondary">Payment Key Management</Text>
        </div>
      </div>
    </div>
  );

  return (
    <PageLayout
      title="Payment Keys"
      desc="Manage payment gateway keys and configurations"
      avatar={user?.avatar}
      headerContent={headerContent}
    >
      <Card>
        <div style={{ marginBottom: '16px', textAlign: 'right' }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAdd}
            style={{ marginRight: '16px' }}
          >
            Add Payment Key
          </Button>
        </div>
        
        <div style={{ marginBottom: '16px' }}>
          <Search
            placeholder="Search payment keys..."
            allowClear
            style={{ width: '50%' }}
            onSearch={handleSearch}
            enterButton={<SearchOutlined />}
          />
        </div>

        <Table
          columns={columns}
          dataSource={paymentKeys}
          rowKey="payment_keys_id"
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ x: 1000 }}
          size="middle"
        />
      </Card>
    </PageLayout>
  );
};

export default PaymentKeyList;